import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  InputAdornment,
  Stack,
  Skeleton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  MoreVert,
  PersonAdd,
  Search,
  FilterList,
  Refresh,
  VpnKey,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DataTableShell,
  FormDialog,
  InlineMessage,
  MetricCard,
  PageHeader,
  StatusChip,
  StatusTone,
  PageContainer,
} from '../components/ui';
import { AdminUserApiKeysDialog } from '../components/AdminUserApiKeysDialog';
import { useIsMobile } from '../hooks/useIsMobile';

// User management uses the API endpoint with /api/admin/users

interface User {
  uid: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'super_admin' | 'user' | 'moderator';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  emailVerified: boolean;
  organizationName?: string;
  organizationId?: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  suspendedUsers: number;
  pendingUsers: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  adminUsers: number;
  superAdminUsers: number;
  regularUsers: number;
  moderatorUsers: number;
}

interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

const isPlatformAdminRole = (role: User['role']) => role === 'admin' || role === 'super_admin';

/** A profile with no display name often falls back to the email; treat that as not set. */
const hasName = (user: User) =>
  Boolean(user.displayName && user.displayName.trim() && user.displayName.trim() !== user.email);

const NO_ORG_NOTE_ADMIN = 'Platform admins work for Motzu and are not part of a customer organization.';
const NO_ORG_NOTE_USER =
  'This account is not a member of any organization yet, for example a signup that has not finished onboarding or an invitation not yet accepted.';

const renderUserName = (user: User) =>
  hasName(user) ? (
    user.displayName
  ) : (
    <Tooltip title="This user has not set a display name.">
      <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        Name not set
      </Box>
    </Tooltip>
  );

const renderNoOrganization = (user: User) => (
  <Tooltip title={isPlatformAdminRole(user.role) ? NO_ORG_NOTE_ADMIN : NO_ORG_NOTE_USER}>
    <Box component="span">{isPlatformAdminRole(user.role) ? 'Platform admin (no organization)' : 'No organization'}</Box>
  </Tooltip>
);

/** The API answers an error as `{ error: { message } }` (AppError) or `{ message }`; fall back to the caller's text. */
const apiErrorMessage = (body: any, fallback: string): string => {
  const message = body?.error?.message ?? body?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const formatDate = (value: string | undefined, fallback: string) => {
  if (!value) return fallback;
  const date = new Date(value);
  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
};

export const AdminUserManagementPage: React.FC = () => {
  const { isAdmin, isSuperAdmin, userProfile } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'delete'>('create');
  const [apiKeysUser, setApiKeysUser] = useState<User | null>(null);
  const [menuState, setMenuState] = useState<{ anchor: HTMLElement; user: User } | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'user' as 'admin' | 'super_admin' | 'user' | 'moderator',
    status: 'active' as 'active' | 'inactive' | 'suspended' | 'pending',
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch user statistics
  const { data: stats, isLoading: statsLoading } = useQuery<UserStats>({
    queryKey: ['userStats'],
    queryFn: async () => {
      const { getAuthHeaders, getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/admin/users/stats`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch user stats');
      const result = await response.json();
      return result.data;
    },
  });

  // Fetch users list
  const {
    data: usersData,
    isLoading: usersLoading,
    isError: usersError,
    error: usersErrorDetail,
    refetch,
  } = useQuery<UserListResponse>({
    queryKey: ['users', page, rowsPerPage, searchTerm, roleFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
      });
      
      const { getAuthHeaders, getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/admin/users?${params}`, {
        headers,
      });
      if (!response.ok) {
        const message = await response
          .json()
          .then((body) => body?.error?.message || body?.message)
          .catch(() => undefined);
        throw new Error(message || `Failed to fetch users (HTTP ${response.status})`);
      }
      const result = await response.json();
      const payload = result.data ?? result;
      return {
        users: Array.isArray(payload?.users) ? payload.users : [],
        total: typeof payload?.total === 'number' ? payload.total : 0,
        page: typeof payload?.page === 'number' ? payload.page : page + 1,
        limit: typeof payload?.limit === 'number' ? payload.limit : rowsPerPage,
        hasMore: Boolean(payload?.hasMore),
      };
    },
  });


  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData: any) => {
      const { getAuthHeaders, getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/admin/users`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(apiErrorMessage(body, 'Failed to create user'));
      }
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Failed to create user');
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, userData }: { userId: string; userData: any }) => {
      const { getAuthHeaders, getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(apiErrorMessage(body, 'Failed to update user'));
      }
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Failed to update user');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { getAuthHeaders, getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/admin/users/${userId}`, {
        method: 'DELETE',
        headers,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(apiErrorMessage(body, 'Failed to delete user'));
      }
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Failed to delete user');
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      displayName: '',
      role: 'user',
      status: 'active',
    });
    setSelectedUser(null);
    setFormError(null);
  };

  const handleOpenDialog = (type: 'create' | 'edit' | 'delete', user?: User) => {
    if (user && user.role === 'super_admin' && !isSuperAdmin) {
      return;
    }
    setDialogType(type);
    setSelectedUser(user || null);
    setFormError(null);
    if (type === 'edit' && user) {
      setFormData({
        email: user.email,
        password: '',
        displayName: user.displayName || '',
        role: user.role,
        status: user.status,
      });
    }
    setDialogOpen(true);
  };

  const userMutationPending =
    createUserMutation.isPending ||
    updateUserMutation.isPending ||
    deleteUserMutation.isPending;

  const handleCloseDialog = () => {
    if (userMutationPending) return;
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = () => {
    if (dialogType !== 'delete') {
      const targetEmail = (dialogType === 'edit' ? selectedUser?.email : formData.email)?.toLowerCase() || '';
      if (formData.role === 'super_admin') {
        const isMotzuEmail = targetEmail.endsWith('@motzulabs.com');
        if (!isMotzuEmail) {
          setFormError('Super admin access is restricted to @motzulabs.com email addresses.');
          return;
        }
      }
      setFormError(null);
    }

    if (!isSuperAdmin && formData.role === 'super_admin') {
      setFormError('Super admin privileges are required to assign the super admin role.');
      return;
    }

    if (dialogType === 'create') {
      createUserMutation.mutate(formData);
    } else if (dialogType === 'edit' && selectedUser) {
      if (!isSuperAdmin && formData.role === 'super_admin') {
        setFormError('Super admin privileges are required to assign the super admin role.');
        return;
      }

      // For updates, exclude the password field and email (which shouldn't be changed)
      const { password, email, ...updateData } = formData;
      updateUserMutation.mutate({
        userId: selectedUser.uid,
        userData: updateData,
      });
    } else if (dialogType === 'delete' && selectedUser) {
      deleteUserMutation.mutate(selectedUser.uid);
    }
  };

  const getStatusColor = (status: string): StatusTone => {
    switch (status) {
      case 'active': return 'positive';
      case 'inactive': return 'neutral';
      case 'suspended': return 'problem';
      case 'pending': return 'caution';
      default: return 'neutral';
    }
  };

  const formatRoleLabel = (role: string) => {
    if (!role) return 'User';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getRoleColor = (role: string): StatusTone => {
    switch (role) {
      case 'super_admin': return 'info';
      case 'admin': return 'info';
      case 'moderator': return 'caution';
      case 'user': return 'neutral';
      default: return 'neutral';
    }
  };

  // Icon-only row actions, shared by the mobile list and the desktop table.
  // Each one carries a tooltip and an aria-label that names the user; delete
  // lives in the overflow menu and still goes through the confirm dialog.
  const renderUserActions = (user: User, iconFontSize: 'small' | 'medium') => {
    const locked = !isSuperAdmin && user.role === 'super_admin';
    const name = user.displayName || user.email;
    return (
      <>
        <Tooltip title="Manage API keys">
          <span>
            <IconButton
              aria-label={`Manage API keys for ${name}`}
              onClick={() => setApiKeysUser(user)}
              size="small"
              disabled={locked}
            >
              <VpnKey fontSize={iconFontSize} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit user">
          <span>
            <IconButton
              aria-label={`Edit ${name}`}
              onClick={() => handleOpenDialog('edit', user)}
              size="small"
              disabled={locked}
            >
              <Edit fontSize={iconFontSize} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="More actions">
          <span>
            <IconButton
              aria-label={`More actions for ${name}`}
              aria-haspopup="menu"
              onClick={(event) => setMenuState({ anchor: event.currentTarget, user })}
              size="small"
              disabled={locked}
            >
              <MoreVert fontSize={iconFontSize} />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  };

  if (!isAdmin) {
    return (
      <PageContainer>
        <InlineMessage severity="error">Access denied. Admin privileges required.</InlineMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="User Management"
        description="Manage platform users, roles, and account lifecycle."
        actionLabel="Add User"
        actionIcon={<PersonAdd />}
        onAction={() => handleOpenDialog('create')}
      />

      {/* Statistics Cards */}
      <Box
        sx={{
          mb: 4,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {[
          {
            label: 'Total Users',
            value: stats?.totalUsers || 0,
            color: 'inherit',
          },
          {
            label: 'Active Users',
            value: stats?.activeUsers || 0,
            color: 'success.main',
          },
          {
            label: 'New This Week',
            value: stats?.newUsersThisWeek || 0,
            color: 'primary.main',
          },
          {
            label: 'Admin Users',
            value: stats?.adminUsers || 0,
            color: 'info.main',
          },
        ].map((stat, index) => (
          <MetricCard
            key={index}
            label={stat.label}
            value={stat.value}
            color={stat.color}
            loading={statsLoading}
          />
        ))}
      </Box>

      {/* Filters: one compact row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 260px', minWidth: 220 }}
          inputProps={{ 'aria-label': 'Search users' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Role</InputLabel>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} label="Role">
            <MenuItem value="">All Roles</MenuItem>
            {isSuperAdmin && <MenuItem value="super_admin">Super Admin</MenuItem>}
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="moderator">Moderator</MenuItem>
            <MenuItem value="user">User</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterList />}
            disabled={!searchTerm && !roleFilter && !statusFilter}
            onClick={() => {
              setSearchTerm('');
              setRoleFilter('');
              setStatusFilter('');
            }}
          >
            Clear filters
          </Button>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Users Table */}
      {usersError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {usersErrorDetail instanceof Error
            ? usersErrorDetail.message
            : 'Failed to load users'}
        </Alert>
      )}
      <DataTableShell>
      {isMobile ? (
        <Box>
          {usersLoading ? (
            [...Array(5)].map((_, index) => (
              <Box key={index} sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="rectangular" width={120} height={24} sx={{ borderRadius: 1, mt: 1 }} />
              </Box>
            ))
          ) : (
            <Stack divider={<Divider />}>
              {(usersData?.users ?? []).map((user) => (
                <Box key={user.uid} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {renderUserName(user)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                        {user.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={user.organizationName ? 'textPrimary' : 'textSecondary'}
                        sx={{ mt: 0.5, fontWeight: user.organizationName ? 500 : 400 }}
                      >
                        {user.organizationName || renderNoOrganization(user)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      {renderUserActions(user, 'small')}
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <StatusChip
                      label={formatRoleLabel(user.role)}
                      tone={getRoleColor(user.role)}
                      size="small"
                    />
                    <StatusChip
                      label={user.status}
                      tone={getStatusColor(user.status)}
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Created: {formatDate(user.createdAt, 'Unknown')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Last login: {formatDate(user.lastLoginAt, 'Never')}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      ) : (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersLoading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton variant="circular" width={32} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              (usersData?.users ?? []).map((user) => (
                <TableRow key={user.uid}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {renderUserName(user)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.organizationName ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.organizationName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {renderNoOrganization(user)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={formatRoleLabel(user.role)}
                      tone={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={user.status}
                      tone={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt, 'Unknown')}</TableCell>
                  <TableCell>{formatDate(user.lastLoginAt, 'Never')}</TableCell>
                  <TableCell>{renderUserActions(user, 'medium')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={usersData?.total || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={isMobile ? 'Rows:' : undefined}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: { xs: 1, sm: 2 } },
        }}
      />
      </DataTableShell>

      {/* Row overflow menu: delete opens the confirm dialog below */}
      <Menu
        anchorEl={menuState?.anchor}
        open={!!menuState}
        onClose={() => setMenuState(null)}
      >
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            const target = menuState?.user;
            setMenuState(null);
            if (target) handleOpenDialog('delete', target);
          }}
        >
          Delete user…
        </MenuItem>
      </Menu>

      {/* User Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        title={
          dialogType === 'create'
            ? 'Create New User'
            : dialogType === 'edit'
              ? 'Edit User'
              : 'Delete User'
        }
        submitLabel={
          dialogType === 'delete' && deleteUserMutation.isPending
            ? 'Deleting…'
            : dialogType === 'create'
              ? 'Create'
              : dialogType === 'edit'
                ? 'Update'
                : 'Delete'
        }
        submitColor={dialogType === 'delete' ? 'error' : 'primary'}
        loading={userMutationPending}
      >
        {dialogType === 'delete' ? (
          <Stack spacing={2}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Typography>
              Are you sure you want to delete user "{selectedUser?.email}"? This action cannot be undone.
            </Typography>
          </Stack>
        ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={dialogType === 'edit'}
              />
              <TextField
                fullWidth
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              />
              {dialogType === 'create' && (
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              )}
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    label="Role"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="moderator">Moderator</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {isSuperAdmin && <MenuItem value="super_admin">Super Admin</MenuItem>}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
        )}
      </FormDialog>

      {apiKeysUser && (
        <AdminUserApiKeysDialog
          open={!!apiKeysUser}
          userId={apiKeysUser.uid}
          userLabel={
            apiKeysUser.organizationName
              ? `${apiKeysUser.displayName || apiKeysUser.email} · ${apiKeysUser.organizationName}`
              : apiKeysUser.displayName || apiKeysUser.email
          }
          onClose={() => setApiKeysUser(null)}
        />
      )}
    </PageContainer>
  );
};
